import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { fetchRegistrationStatus } from '../../services/inviteService';

// Webhook delivery is typically <2s but can spike to ~10-15s under load. We
// poll every 2s for up to 30s, then surface a friendly error with retry.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const OnboardingSuccess = () => {
  const [searchParams] = useSearchParams();
  const pendingId = searchParams.get('pendingId');
  const sessionId = searchParams.get('sessionId');

  const [status, setStatus] = useState('polling');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!pendingId) {
      setStatus('error');
      setMessage('Lipseste identificatorul inregistrarii (pendingId).');
      return undefined;
    }

    let cancelled = false;
    let timer;

    const poll = async () => {
      if (cancelled) return;
      const res = await fetchRegistrationStatus(pendingId);
      if (cancelled) return;
      if (res.completed) {
        setStatus('success');
        setMessage(
          'Contul tau a fost creat. Te poti autentifica acum cu emailul si parola alese.',
        );
        // Give the user 1.5s to read the message, then push them to /login.
        timer = setTimeout(() => navigate('/login'), 1500);
        return;
      }
      const elapsed = Date.now() - startedAt.current;
      if (elapsed >= POLL_TIMEOUT_MS) {
        setStatus('timeout');
        setMessage(
          'Plata a reusit dar contul tau este inca in procesare. Verifica emailul in cateva minute sau apasa "Reincearca".',
        );
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pendingId, navigate]);

  const handleRetry = () => {
    startedAt.current = Date.now();
    setStatus('polling');
    setMessage('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        {status === 'polling' && (
          <div className="flex flex-col items-center">
            <Loader2 className="animate-spin w-12 h-12 text-indigo-500" />
            <h1 className="text-xl font-bold text-gray-800 mt-4">
              Se creeaza contul tau…
            </h1>
            <p className="text-gray-500 mt-2">
              Procesam plata si configuram abonamentul. Acest pas dureaza de obicei
              cateva secunde.
            </p>
            {sessionId && (
              <p className="text-xs text-gray-400 mt-3">
                Referinta plata: {sessionId.substring(0, 12)}…
              </p>
            )}
          </div>
        )}
        {status === 'success' && (
          <div className="flex flex-col items-center">
            <CheckCircle className="w-14 h-14 text-green-500" />
            <h1 className="text-2xl font-bold text-green-600 mt-4">Bun venit!</h1>
            <p className="text-gray-600 mt-2">{message}</p>
          </div>
        )}
        {status === 'timeout' && (
          <div className="flex flex-col items-center">
            <AlertTriangle className="w-14 h-14 text-amber-500" />
            <h1 className="text-2xl font-bold text-amber-700 mt-4">
              Inca procesam plata
            </h1>
            <p className="text-gray-600 mt-2">{message}</p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleRetry}
                className="px-5 py-2 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600"
              >
                Reincearca
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Contacteaza suportul
              </button>
            </div>
          </div>
        )}
        {status === 'error' && (
          <div className="flex flex-col items-center">
            <AlertTriangle className="w-14 h-14 text-red-500" />
            <h1 className="text-2xl font-bold text-red-600 mt-4">Eroare</h1>
            <p className="text-gray-600 mt-2">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-5 px-5 py-2 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
            >
              Inapoi la Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingSuccess;
